import React, {Fragment, memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useRecoilState} from 'recoil';
import {makeRequest} from '../../utils/httpRequest';
import Config from '../../core/config';
import {loadingAtom} from '../../atoms';
import {UserPermission, CompanyPermission} from '../../utils/utils';
import Loading from '../../core/Loading';
import {usePermission} from '../../core/PermissionContext';
import {getCurrentUserId} from '../../utils/auth';
import DeleteButton from '../common/DeleteButton';
import {showConfirm, showAlert} from '../../core/dialog';
import ShoppingListItemSelectAsync from './ShoppingListItemSelectAsync';
import './shoppingList.css';
import {
    getDefaultShoppingListName,
    getShoppingItemKey,
    isShoppingItemFilled,
    nextShoppingItemClientKey,
    resolveSectorColor,
    sectorTextColor,
    sortShoppingListItems,
    SUPERMARKET_SECTORS,
} from './shoppingListUtils';

const apiUrl = Config.apiUrl;

const newItem = () => ({
    id: undefined,
    _clientKey: nextShoppingItemClientKey(),
    catalogItem: {id: undefined, name: ''},
    quantity: 1,
    observation: '',
    supermarketSector: '',
    purchased: false,
    sortOrder: 0,
});

const newListEntity = () => ({
    id: undefined,
    name: getDefaultShoppingListName(),
    sharedUserIds: [],
    defaultTemplate: false,
    ownerUserId: undefined,
});

const AUTO_SAVE_DELAY_MS = 600;
const PURCHASE_IDLE_MS = 1000;
const PURCHASED_ANIMATION_MS = 1000;
// Chave-sentinela para tratar o título da lista como uma "ação" no commit adiado.
const LIST_TITLE_KEY = '__list_title__';

function parseQuantity(value) {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
}

/**
 * Extrai nome e quantidade de um texto livre.
 * Detecta número no início ("2 leite", "3x leite") ou no fim ("leite 2", "leite x2").
 * Retorna hasExplicitQuantity=true apenas quando o número foi detectado no texto.
 */
function parseItemText(rawText) {
    const text = (rawText || '').trim();
    if (!text) return {name: '', quantity: 1, hasExplicitQuantity: false};

    // Número (com 'x' opcional) no início: "2 leite", "3x leite"
    let m = text.match(/^(\d+)\s*x?\s+(.+)$/i);
    if (m) {
        const qty = parseInt(m[1], 10);
        const name = m[2].trim();
        if (qty >= 1 && name) return {name, quantity: qty, hasExplicitQuantity: true};
    }

    // Número (com 'x' opcional) no fim: "leite 2", "leite x2", "leite 2x"
    m = text.match(/^(.+)\s+x?(\d+)x?$/i);
    if (m) {
        const name = m[1].trim();
        const qty = parseInt(m[2], 10);
        if (qty >= 1 && name) return {name, quantity: qty, hasExplicitQuantity: true};
    }

    return {name: text, quantity: 1, hasExplicitQuantity: false};
}

function isShoppingItemCommitted(item) {
    return !!(item?.catalogItem?.id && String(item.catalogItem.id).trim() !== '');
}

/**
 * Pós-save: apenas atribui IDs do servidor a itens recém-criados (sem id local).
 * Não reimporta nem re-adiciona itens — o estado local é a fonte de verdade após o save.
 * Isso evita que itens excluídos durante um save em-flight voltem a aparecer.
 */
function assignServerIds(prevItems, serverItems) {
    const serverByName = new Map(
        (serverItems || [])
            .filter((si) => si.id && si.itemName)
            .map((si) => [si.itemName.trim().toLowerCase(), si]),
    );
    let changed = false;
    const result = prevItems.map((item) => {
        if (item.id || !isShoppingItemCommitted(item)) return item;
        const si = serverByName.get((item.catalogItem.name || '').trim().toLowerCase());
        if (!si?.id) return item;
        changed = true;
        return {...item, id: si.id, _clientKey: `db-${si.id}`, sortOrder: si.sortOrder ?? item.sortOrder};
    });
    return changed ? result : prevItems;
}

/**
 * Mescla o estado do servidor no estado local para sincronização concorrente.
 * - Itens presentes só no servidor (adicionados por outro usuário) → incorporados ao local.
 * - Itens presentes só no local (adicionados localmente ou excluídos por outro) → mantidos.
 * - Itens em ambos: campos do servidor ganham, EXCETO os marcados em localDirtyKeys (editados localmente).
 *   Item editado localmente prevalece INTEGRALMENTE (inclui 'purchased') até o próximo save.
 */
function mergePollResult(prevItems, serverItems, localDirtyKeys, deletedIds = new Set()) {
    const serverById = new Map((serverItems || []).filter((si) => si.id).map((si) => [si.id, si]));
    const prevById = new Map(prevItems.filter((i) => i.id).map((i) => [i.id, i]));

    const updated = [];
    prevItems.forEach((item) => {
        if (!item.id) {
            updated.push(item); // Item novo local (sem id ainda) — mantém
            return;
        }
        const si = serverById.get(item.id);
        if (!si) {
            // Item com id que sumiu do servidor → removido por outro usuário: reflete a remoção.
            // Exceção: edição local pendente nesse item → local prevalece (mantém).
            if (localDirtyKeys.has(getShoppingItemKey(item))) {
                updated.push(item);
            }
            return;
        }
        if (localDirtyKeys.has(getShoppingItemKey(item))) {
            // Item editado localmente: estado local prevalece integralmente até o próximo save
            updated.push(item);
            return;
        }
        // Item não editado localmente: atualiza tudo do servidor
        const serverQty = parseQuantity(si.quantity);
        const unchanged = (
            item.catalogItem?.name === si.itemName
            && item.quantity === serverQty
            && item.observation === (si.observation || '')
            && item.supermarketSector === (si.supermarketSector || '')
            && item.purchased === !!si.purchased
            && item.sortOrder === (si.sortOrder ?? 0)
        );
        updated.push(unchanged ? item : {
            ...item,
            catalogItem: {id: si.itemName, name: si.itemName},
            quantity: serverQty,
            observation: si.observation || '',
            supermarketSector: si.supermarketSector || '',
            purchased: !!si.purchased,
            sortOrder: si.sortOrder ?? 0,
        });
    });

    // Itens presentes só no servidor (adicionados por outro usuário).
    // Ignora IDs com remoção local pendente (ainda não salva): senão o poll que ocorre
    // antes do save re-adicionaria o item que o usuário acabou de remover.
    const newFromServer = [];
    (serverItems || []).forEach((si, index) => {
        if (si.id && !prevById.has(si.id) && !deletedIds.has(si.id)) {
            newFromServer.push({
                id: si.id,
                _clientKey: `db-${si.id}`,
                catalogItem: {id: si.itemName, name: si.itemName},
                quantity: parseQuantity(si.quantity),
                observation: si.observation || '',
                supermarketSector: si.supermarketSector || '',
                purchased: !!si.purchased,
                sortOrder: si.sortOrder ?? index,
            });
        }
    });

    if (
        newFromServer.length === 0
        && updated.length === prevItems.length
        && updated.every((item, i) => item === prevItems[i])
    ) {
        return prevItems; // Nenhuma mudança, preserva referência
    }
    return [...updated, ...newFromServer];
}

function buildItemsPayload(itemsList) {
    const filled = (itemsList || []).filter((item) => isShoppingItemCommitted(item));
    const ordered = sortShoppingListItems(filled, {column: null, direction: 'asc'});
    return ordered.map((item, index) => ({
        id: item.id,
        itemName: item.catalogItem.name.trim(),
        quantity: String(parseQuantity(item.quantity)),
        observation: item.observation || '',
        supermarketSector: item.supermarketSector || '',
        purchased: !!item.purchased,
        sortOrder: index,
    }));
}

function mapServerItemsToState(serverItems) {
    const loadedItems = (serverItems || []).map((item, index) => ({
        id: item.id,
        _clientKey: item.id ? `db-${item.id}` : nextShoppingItemClientKey(),
        catalogItem: {id: item.itemName, name: item.itemName},
        quantity: parseQuantity(item.quantity),
        observation: item.observation || '',
        supermarketSector: item.supermarketSector || '',
        purchased: !!item.purchased,
        sortOrder: item.sortOrder ?? index,
    }));
    return loadedItems;
}

const rebuildExpandedObsKeys = (list) => {
    const keys = new Set();
    (list || []).forEach((item) => {
        if (item.observation && item.observation.trim() !== '') {
            keys.add(getShoppingItemKey(item));
        }
    });
    return keys;
};

const SortableHeader = ({column, label, sortConfig, onSort, style, className = ''}) => {
    const isPrimary = sortConfig.primary.column === column;
    const isSecondary = sortConfig.secondary?.column === column;
    let indicator = '';
    if (isPrimary) {
        indicator = sortConfig.primary.direction === 'asc' ? ' ▲' : ' ▼';
    } else if (isSecondary) {
        indicator = sortConfig.secondary.direction === 'asc' ? ' ²▲' : ' ²▼';
    }
    return (
        <th
            style={style}
            className={`shopping-list-sortable-th ${className}`.trim()}
            onClick={() => onSort(column)}
            title="Clique para ordenar"
        >
            {label}{indicator}
        </th>
    );
};

const ShoppingListItemRow = memo(({
    itemKey,
    item,
    obsExpanded,
    checkboxChecked,
    isPurchasingAnim,
    purchased,
    isPendingUnmark,
    rowClass,
    namePurchasedClass,
    excludeNames,
    itemInputRefs,
    sectorColorMap,
    onTogglePurchased,
    onItemClick,
    onInputValueChange,
    onBlur,
    onItemChange,
    onToggleObs,
    onRemove,
    onOpenSectorPicker,
}) => {
    const filled = isShoppingItemFilled(item);
    const qty = parseQuantity(item.quantity);
    const sectorColor = item.supermarketSector
        ? resolveSectorColor(item.supermarketSector, sectorColorMap)
        : null;

    return (
        <Fragment>
            <tr className={rowClass}>
                <td className="shopping-list-purchased-cell">
                    {filled && (
                        <input
                            type="checkbox"
                            checked={checkboxChecked}
                            disabled={isPurchasingAnim}
                            onChange={() => onTogglePurchased(itemKey)}
                            title={purchased || isPendingUnmark ? 'Desmarcar como comprado' : 'Marcar como comprado'}
                            data-test={`shopping-list-purchased-${itemKey}`}
                        />
                    )}
                </td>
                <td className={namePurchasedClass}>
                    <div className="shopping-list-item-cell">
                        <ShoppingListItemSelectAsync
                            ref={(el) => {
                                itemInputRefs.current[itemKey] = el;
                            }}
                            style={{width: '100%', minWidth: '90px', menuWidth: '180px'}}
                            textValue={item.catalogItem?.name ?? ''}
                            excludeNames={excludeNames}
                            onItemClick={(name) => onItemClick(name, itemKey)}
                            onInputValueChange={(inputValue) => onInputValueChange(inputValue, itemKey)}
                            onBlur={() => onBlur(itemKey)}
                            dataTest={`shopping-list-item-select-${itemKey}`}
                        />
                    </div>
                </td>
                <td>
                    {filled && (
                        <button
                            type="button"
                            className="shopping-list-sector-btn"
                            style={{backgroundColor: item.supermarketSector ? sectorColor : '#cccccc'}}
                            onClick={() => onOpenSectorPicker(itemKey)}
                            data-test={`shopping-list-sector-${itemKey}`}
                            title={item.supermarketSector || 'Sem setor'}
                            aria-label={`Setor: ${item.supermarketSector || 'nenhum'}`}
                        />
                    )}
                </td>
                <td>
                    {filled && (
                        <div className="shopping-list-qty-stepper" data-test={`shopping-list-qty-${itemKey}`}>
                            <button
                                type="button"
                                className="shopping-list-qty-btn"
                                onClick={() => onItemChange(itemKey, 'quantity', Math.max(1, qty - 1))}
                                tabIndex={-1}
                                aria-label="Diminuir quantidade"
                            >−</button>
                            <span className="shopping-list-qty-value">{qty}</span>
                            <button
                                type="button"
                                className="shopping-list-qty-btn"
                                onClick={() => onItemChange(itemKey, 'quantity', qty + 1)}
                                tabIndex={-1}
                                aria-label="Aumentar quantidade"
                            >+</button>
                        </div>
                    )}
                </td>
                <td className="shopping-list-item-actions-cell">
                    {filled && (
                        <>
                            <button
                                type="button"
                                className="delivery-expand-btn shopping-list-expand-btn"
                                onClick={() => onToggleObs(itemKey)}
                                title={obsExpanded ? 'Recolher observação' : 'Adicionar observação'}
                                data-test={`shopping-list-obs-toggle-${itemKey}`}
                            >
                                {obsExpanded ? '▲' : '▼'}
                            </button>
                            <DeleteButton
                                className="remove-button"
                                onClick={() => onRemove(itemKey)}
                                data-test={`remove-shopping-item-${itemKey}`}
                            />
                        </>
                    )}
                </td>
            </tr>
            {filled && obsExpanded && (
                <tr className={`shopping-list-obs-expanded-row ${rowClass}`}>
                    <td colSpan={5} className="shopping-list-obs-expanded-cell">
                        <label className="shopping-list-obs-expanded-label">Observação</label>
                        <input
                            type="text"
                            className="shopping-list-item-obs-expanded"
                            value={item.observation || ''}
                            onChange={(e) => onItemChange(itemKey, 'observation', e.target.value)}
                            placeholder="Observação"
                            data-test={`shopping-list-obs-${itemKey}`}
                        />
                    </td>
                </tr>
            )}
        </Fragment>
    );
});

const ShoppingListManager = () => {
    const {hasUserPermission, hasCompanyPermission, loadingPermissions} = usePermission();
    const navigate = useNavigate();
    const [, setLoading] = useRecoilState(loadingAtom);
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('id');
    const currentUserId = getCurrentUserId();

    const [formData, setFormData] = useState(newListEntity());
    const [items, setItems] = useState([]);
    const [newItemText, setNewItemText] = useState('');
    const [sortConfig, setSortConfig] = useState({
        primary: {column: 'sector', direction: 'asc'},
        secondary: {column: 'item', direction: 'asc'},
    });
    const [expandedObsKeys, setExpandedObsKeys] = useState(new Set());
    const [purchasePendingKeys, setPurchasePendingKeys] = useState(() => new Map());
    const [purchasingAnimKeys, setPurchasingAnimKeys] = useState(new Set());
    const [purchasingAnimModes, setPurchasingAnimModes] = useState(() => new Map());
    const [committingAnimKeys, setCommittingAnimKeys] = useState(() => new Set());
    const [saveStatus, setSaveStatus] = useState('');
    const [customSectors, setCustomSectors] = useState([]);
    const [sectorPickerKey, setSectorPickerKey] = useState(null);
    const managerRef = useRef(null);
    const itemInputRefs = useRef({});
    const newItemInputRef = useRef(null);
    const newItemTextRef = useRef('');
    const purchasePendingRef = useRef(new Map());
    const purchaseIdleTimerRef = useRef(null);
    const purchaseAnimTimersRef = useRef({});
    // Commit adiado genérico (edições/adição/remoção): mesma ideia do comprado.
    const pendingCommitRef = useRef(new Map()); // key -> 'edit' | 'remove'
    const editIdleTimerRef = useRef(null);
    const editAnimTimersRef = useRef({});
    const committingAnimKeysRef = useRef(new Set());
    const itemsRef = useRef(items);
    const formDataRef = useRef(formData);
    const purchasingAnimKeysRef = useRef(new Set());
    const skipAutoSaveRef = useRef(true);
    const saveTimerRef = useRef(null);
    const saveInFlightRef = useRef(false);
    const pendingSaveRef = useRef(false);
    const suppressItemBlurCommitRef = useRef(false);
    const localDirtyRef = useRef(new Set());
    const nameDirtyRef = useRef(false);
    const deletedItemIdsRef = useRef(new Set());
    const lastKnownUpdatedAtRef = useRef(null);
    const syncIntervalRef = useRef(null);
    const syncInFlightRef = useRef(false);
    const syncNowRef = useRef(null);

    useEffect(() => {
        itemsRef.current = items;
    }, [items]);

    useEffect(() => {
        formDataRef.current = formData;
    }, [formData]);

    useEffect(() => {
        purchasingAnimKeysRef.current = purchasingAnimKeys;
    }, [purchasingAnimKeys]);

    useEffect(() => {
        committingAnimKeysRef.current = committingAnimKeys;
    }, [committingAnimKeys]);

    useEffect(() => {
        newItemTextRef.current = newItemText;
    }, [newItemText]);

    const persistList = useCallback(async () => {
        const currentForm = formDataRef.current;
        const currentItems = itemsRef.current;
        const name = (currentForm.name || '').trim();
        if (!name) {
            return;
        }
        const payloadItems = buildItemsPayload(currentItems);
        // Lista existente pode ser salva vazia: remover o último item precisa persistir e
        // atualizar o updatedAt. Só bloqueia a CRIAÇÃO de uma lista sem itens.
        if (payloadItems.length === 0 && !currentForm.id) {
            return;
        }

        if (saveInFlightRef.current) {
            pendingSaveRef.current = true;
            return;
        }

        saveInFlightRef.current = true;
        setSaveStatus('Salvando...');

        // Captura o snapshot dos IDs que o cliente conhece ANTES de iniciar o request.
        // Inclui os IDs com remoção pendente: o backend usa knownItemIds para distinguir
        // "excluído pelo cliente" (conhecido e ausente do payload → remove) de "adicionado por
        // outro usuário" (desconhecido → preserva). Sem o ID removido aqui, a exclusão não persiste.
        const knownItemIds = [
            ...currentItems.filter((i) => i.id).map((i) => i.id),
            ...deletedItemIdsRef.current,
        ];

        try {
            const listId = currentForm.id;
            const isCreate = !listId;
            const payload = {
                id: listId,
                name,
                items: payloadItems,
                knownItemIds: isCreate ? [] : knownItemIds,
            };
            if (isCreate) {
                payload.sharedUserIds = [];
            }

            const saved = await makeRequest(
                `${apiUrl}/api/shopping-lists`,
                isCreate ? 'POST' : 'PUT',
                {'Content-Type': 'application/json'},
                JSON.stringify(payload),
            );

            if (saved?.id) {
                skipAutoSaveRef.current = true;
                // Captura dirty/excluídos ANTES de limpar: protege mudanças feitas durante o save em voo
                const dirtySnapshot = new Set(localDirtyRef.current);
                const deletedSnapshot = new Set(deletedItemIdsRef.current);
                localDirtyRef.current.clear();
                deletedItemIdsRef.current.clear();
                nameDirtyRef.current = false;
                if (isCreate) {
                    navigate(`/app/shoppinglist?id=${saved.id}`, {replace: true});
                    const allRows = mapServerItemsToState(saved.items);
                    setExpandedObsKeys(rebuildExpandedObsKeys(allRows));
                    setItems(allRows);
                } else {
                    // 1) Atribui IDs do servidor a itens recém-criados
                    // 2) Mescla itens de outros usuários; em conflito, local prevalece (dirtySnapshot)
                    setItems((prev) => {
                        const withIds = assignServerIds(prev, saved.items);
                        return mergePollResult(withIds, saved.items, dirtySnapshot, deletedSnapshot);
                    });
                }
                setFormData((prev) => ({
                    ...prev,
                    id: saved.id,
                    name: saved.name,
                    sharedUserIds: saved.sharedUserIds || prev.sharedUserIds || [],
                    ownerUserId: saved.ownerUserId ?? prev.ownerUserId ?? currentUserId,
                    defaultTemplate: !!saved.defaultTemplate,
                }));
                if (saved.updatedAt) {
                    lastKnownUpdatedAtRef.current = saved.updatedAt;
                }
                skipAutoSaveRef.current = false;
            }
            setSaveStatus('Salvo');
        } catch (error) {
            console.error('Error auto-saving shopping list:', error);
            setSaveStatus('Erro ao salvar');
        } finally {
            saveInFlightRef.current = false;
            if (pendingSaveRef.current) {
                pendingSaveRef.current = false;
                setTimeout(() => persistList(), 0);
            }
        }
    }, [navigate]);

    const scheduleAutoSave = useCallback(() => {
        if (skipAutoSaveRef.current) {
            return;
        }
        syncNowRef.current?.();
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        saveTimerRef.current = setTimeout(() => {
            persistList();
        }, AUTO_SAVE_DELAY_MS);
    }, [persistList]);

    const hasPurchasePending = () => purchasePendingRef.current.size > 0;

    const clearPurchaseAnimTimer = (key) => {
        if (purchaseAnimTimersRef.current[key]) {
            clearTimeout(purchaseAnimTimersRef.current[key]);
            delete purchaseAnimTimersRef.current[key];
        }
    };

    const clearPurchaseIdleTimer = () => {
        if (purchaseIdleTimerRef.current) {
            clearTimeout(purchaseIdleTimerRef.current);
            purchaseIdleTimerRef.current = null;
        }
    };

    const startPurchaseTransitions = useCallback((transitions) => {
        transitions.forEach(({key, mode}) => {
            clearPurchaseAnimTimer(key);
            setPurchasingAnimKeys((prev) => new Set(prev).add(key));
            setPurchasingAnimModes((prev) => {
                const next = new Map(prev);
                next.set(key, mode);
                return next;
            });
            purchaseAnimTimersRef.current[key] = setTimeout(() => {
                clearPurchaseAnimTimer(key);
                setPurchasingAnimKeys((prev) => {
                    const next = new Set(prev);
                    next.delete(key);
                    return next;
                });
                setPurchasingAnimModes((prev) => {
                    const next = new Map(prev);
                    next.delete(key);
                    return next;
                });
                setItems((prevItems) => prevItems.map((row) => (
                    getShoppingItemKey(row) === key
                        ? {...row, purchased: mode === 'mark'}
                        : row
                )));
                scheduleAutoSave();
            }, PURCHASED_ANIMATION_MS);
        });
    }, [scheduleAutoSave]);

    const startPurchaseIdleCountdown = useCallback(() => {
        clearPurchaseIdleTimer();
        if (!hasPurchasePending()) {
            return;
        }
        purchaseIdleTimerRef.current = setTimeout(() => {
            purchaseIdleTimerRef.current = null;
            const transitions = [...purchasePendingRef.current.entries()].map(([key, mode]) => ({
                key,
                mode,
            }));
            purchasePendingRef.current = new Map();
            setPurchasePendingKeys(new Map());
            if (transitions.length > 0) {
                startPurchaseTransitions(transitions);
            }
        }, PURCHASE_IDLE_MS);
    }, [startPurchaseTransitions]);

    const cancelPurchaseTransition = useCallback((key) => {
        clearPurchaseAnimTimer(key);
        setPurchasingAnimKeys((prev) => {
            if (!prev.has(key)) {
                return prev;
            }
            const next = new Set(prev);
            next.delete(key);
            return next;
        });
        setPurchasingAnimModes((prev) => {
            if (!prev.has(key)) {
                return prev;
            }
            const next = new Map(prev);
            next.delete(key);
            return next;
        });
        if (!purchasePendingRef.current.has(key)) {
            return;
        }
        const nextPending = new Map(purchasePendingRef.current);
        nextPending.delete(key);
        purchasePendingRef.current = nextPending;
        setPurchasePendingKeys(nextPending);
        if (nextPending.size === 0) {
            clearPurchaseIdleTimer();
        } else {
            startPurchaseIdleCountdown();
        }
    }, [startPurchaseIdleCountdown]);

    // --- Commit adiado genérico (edições/adição/remoção): mesmo padrão do comprado ---
    const hasPendingCommit = () => pendingCommitRef.current.size > 0;

    const clearEditAnimTimer = (key) => {
        if (editAnimTimersRef.current[key]) {
            clearTimeout(editAnimTimersRef.current[key]);
            delete editAnimTimersRef.current[key];
        }
    };

    const clearEditIdleTimer = () => {
        if (editIdleTimerRef.current) {
            clearTimeout(editIdleTimerRef.current);
            editIdleTimerRef.current = null;
        }
    };

    const startEditTransitions = useCallback((transitions) => {
        transitions.forEach(({key, type}) => {
            clearEditAnimTimer(key);
            setCommittingAnimKeys((prev) => new Set(prev).add(key));
            editAnimTimersRef.current[key] = setTimeout(() => {
                clearEditAnimTimer(key);
                setCommittingAnimKeys((prev) => {
                    const next = new Set(prev);
                    next.delete(key);
                    return next;
                });
                if (type === 'remove') {
                    // A remoção de fato só acontece ao fim do fade.
                    setItems((prevItems) => {
                        const idx = prevItems.findIndex((row) => getShoppingItemKey(row) === key);
                        if (idx < 0) return prevItems;
                        if (prevItems[idx].id) {
                            deletedItemIdsRef.current.add(prevItems[idx].id);
                        }
                        const result = prevItems.filter((_, i) => i !== idx);
                        setExpandedObsKeys(rebuildExpandedObsKeys(result));
                        return result;
                    });
                } else {
                    // 'edit'/'title': o valor já está aplicado. Ao fim do fade, libera a
                    // reordenação congelada da troca de setor (remove _sortSector) → reordena agora.
                    setItems((prevItems) => {
                        let changed = false;
                        const next = prevItems.map((row) => {
                            if (getShoppingItemKey(row) === key && row._sortSector !== undefined) {
                                changed = true;
                                const rest = {...row};
                                delete rest._sortSector;
                                return rest;
                            }
                            return row;
                        });
                        return changed ? next : prevItems;
                    });
                }
                scheduleAutoSave();
            }, PURCHASED_ANIMATION_MS);
        });
    }, [scheduleAutoSave]);

    const startEditIdleCountdown = useCallback(() => {
        clearEditIdleTimer();
        if (!hasPendingCommit()) {
            return;
        }
        editIdleTimerRef.current = setTimeout(() => {
            editIdleTimerRef.current = null;
            const transitions = [...pendingCommitRef.current.entries()].map(([key, type]) => ({key, type}));
            pendingCommitRef.current = new Map();
            if (transitions.length > 0) {
                startEditTransitions(transitions);
            }
        }, PURCHASE_IDLE_MS);
    }, [startEditTransitions]);

    // Registra a ação como pendente (o valor já foi aplicado no estado local) e arma a
    // janela de ociosidade. Ao ficar ocioso → fade → persiste. Sem cancelamento.
    const registerPendingCommit = useCallback((key, type) => {
        const existing = pendingCommitRef.current.get(key);
        // 'remove' tem prioridade: se a linha vai sumir, não rebaixa para 'edit'.
        pendingCommitRef.current.set(key, existing === 'remove' ? 'remove' : type);
        if (key !== LIST_TITLE_KEY) {
            localDirtyRef.current.add(key);
        }
        setTimeout(() => startEditIdleCountdown(), 0);
    }, [startEditIdleCountdown]);

    useEffect(() => {
        const el = managerRef.current;
        if (!el) {
            return undefined;
        }
        const onInteraction = () => {
            if (hasPurchasePending()) {
                startPurchaseIdleCountdown();
            }
            if (hasPendingCommit()) {
                startEditIdleCountdown();
            }
        };
        const events = ['pointerdown', 'keydown', 'input', 'change', 'touchstart'];
        events.forEach((eventName) => el.addEventListener(eventName, onInteraction, true));
        return () => {
            events.forEach((eventName) => el.removeEventListener(eventName, onInteraction, true));
        };
    }, [startPurchaseIdleCountdown, startEditIdleCountdown]);

    useEffect(() => () => {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
        }
        clearPurchaseIdleTimer();
        Object.keys(purchaseAnimTimersRef.current).forEach(clearPurchaseAnimTimer);
        clearEditIdleTimer();
        Object.keys(editAnimTimersRef.current).forEach(clearEditAnimTimer);
        if (syncIntervalRef.current) {
            clearInterval(syncIntervalRef.current);
        }
    }, []);

    const enableAutoSaveAndSchedule = useCallback(() => {
        skipAutoSaveRef.current = false;
        setTimeout(() => scheduleAutoSave(), 0);
    }, [scheduleAutoSave]);

    const fetchAndMerge = useCallback(async (listId) => {
        if (!listId || syncInFlightRef.current) return;
        syncInFlightRef.current = true;
        try {
            const since = lastKnownUpdatedAtRef.current;
            // Se 'since' é conhecido, faz checagem leve primeiro
            if (since) {
                const check = await makeRequest(
                    `${apiUrl}/api/shopping-lists/${listId}/has-changes?since=${encodeURIComponent(since)}`,
                    'GET',
                );
                // Atualiza o 'since' com a precisão de milissegundos retornada pelo servidor
                if (check?.updatedAt) {
                    lastKnownUpdatedAtRef.current = check.updatedAt;
                }
                if (!check?.hasChanges) return;
            }
            // Busca lista completa
            const response = await makeRequest(`${apiUrl}/api/shopping-lists/id=${listId}`, 'GET');
            if (!response?.items) return;
            // Título: aplica o remoto, exceto com edição local pendente/efetivando do título.
            const titlePending = nameDirtyRef.current
                || pendingCommitRef.current.has(LIST_TITLE_KEY)
                || committingAnimKeysRef.current.has(LIST_TITLE_KEY);
            if (!titlePending && response.name != null) {
                setFormData((prev) => (prev.name === response.name ? prev : {...prev, name: response.name}));
            }
            // Protege do poll também as chaves com commit pendente ou em animação (o
            // localDirtyRef é limpo no save, mas essas edições ainda não foram efetivadas).
            const protectedKeys = new Set([
                ...localDirtyRef.current,
                ...pendingCommitRef.current.keys(),
                ...committingAnimKeysRef.current,
            ]);
            setItems((prev) => mergePollResult(
                prev, response.items, protectedKeys, deletedItemIdsRef.current,
            ));
            if (response.updatedAt) {
                lastKnownUpdatedAtRef.current = response.updatedAt;
            }
            setSaveStatus('Atualizado');
            setTimeout(() => setSaveStatus(''), 2000);
        } catch (error) {
            console.error('Error syncing shopping list:', error);
        } finally {
            syncInFlightRef.current = false;
        }
    }, []);

    const syncNow = useCallback(() => {
        const listId = formDataRef.current?.id;
        if (listId) fetchAndMerge(listId);
    }, [fetchAndMerge]);

    syncNowRef.current = syncNow;

    // Poll a cada 3s; busca completa só quando has-changes=true
    useEffect(() => {
        const listId = id;
        if (!listId) return undefined;
        const POLL_MS = 3000;
        const poll = () => {
            if (document.visibilityState === 'visible') {
                fetchAndMerge(listId);
            }
        };
        syncIntervalRef.current = setInterval(poll, POLL_MS);
        const onVisibility = () => {
            if (document.visibilityState === 'visible') fetchAndMerge(listId);
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            clearInterval(syncIntervalRef.current);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [id, fetchAndMerge]);

    const displayItems = useMemo(
        () => sortShoppingListItems(items, sortConfig),
        [items, sortConfig],
    );

    // --- Setores: cadastro próprio (cores) somado aos padrão ---
    const loadSectors = useCallback(async () => {
        try {
            const res = await makeRequest(`${apiUrl}/api/supermarket-sectors`, 'GET');
            setCustomSectors(Array.isArray(res) ? res : []);
        } catch (error) {
            console.error('Error loading supermarket sectors:', error);
        }
    }, []);

    useEffect(() => {
        loadSectors();
    }, [loadSectors]);

    // Mapa nome(normalizado)→cor dos setores custom (referência estável para o memo das linhas).
    const sectorColorMap = useMemo(() => {
        const map = {};
        customSectors.forEach((s) => {
            const name = (s.name || '').trim().toLowerCase();
            if (name) map[name] = s.color || undefined;
        });
        return map;
    }, [customSectors]);

    // Opções do dropdown: padrão + custom (sem duplicar por nome, case-insensitive).
    const sectorOptions = useMemo(() => {
        const names = [...SUPERMARKET_SECTORS];
        const seen = new Set(names.map((n) => n.trim().toLowerCase()));
        customSectors.forEach((s) => {
            const name = (s.name || '').trim();
            const key = name.toLowerCase();
            if (name && !seen.has(key)) {
                seen.add(key);
                names.push(name);
            }
        });
        return names;
    }, [customSectors]);

    const openSectorPicker = useCallback((key) => setSectorPickerKey(key), []);

    const itemNamesSignature = useMemo(
        () => items.map((i) => (i.catalogItem?.id ? i.catalogItem.name : '')).join('|'),
        [items],
    );

    const filledItemNamesMap = useMemo(() => {
        const filled = items.filter((i) => i.catalogItem?.id);
        const map = {};
        items.forEach((item) => {
            const key = getShoppingItemKey(item);
            map[key] = filled
                .filter((i) => getShoppingItemKey(i) !== key)
                .map((i) => i.catalogItem.name)
                .filter(Boolean);
        });
        return map;
    // itemNamesSignature changes only when names change, not on qty/sector/obs edits
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemNamesSignature]);

    const allFilledNames = useMemo(
        () => items.filter((i) => i.catalogItem?.id).map((i) => i.catalogItem.name).filter(Boolean),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [itemNamesSignature],
    );

    const handleAddNewItem = useCallback((explicitName) => {
        const parsed = parseItemText(newItemTextRef.current);
        // Se o usuário selecionou do autocomplete, usa o nome selecionado mas mantém a quantidade detectada
        const name = explicitName != null ? explicitName.trim() : parsed.name;
        const {quantity, hasExplicitQuantity} = parsed;
        if (!name) return;
        const normalized = name.toLowerCase();
        const dupItem = itemsRef.current.find((i) =>
            i.catalogItem?.id && (i.catalogItem.name || '').trim().toLowerCase() === normalized
        );
        if (dupItem) {
            if (hasExplicitQuantity) {
                // Atualiza a quantidade do item existente
                const dupKey = getShoppingItemKey(dupItem);
                setItems((prev) => prev.map((i) =>
                    getShoppingItemKey(i) === dupKey ? {...i, quantity} : i
                ));
                setNewItemText('');
                registerPendingCommit(dupKey, 'edit');
                setTimeout(() => newItemInputRef.current?.focus?.(), 50);
            } else {
                showAlert('Este item já está na lista.');
            }
            return;
        }
        const maxSortOrder = itemsRef.current.reduce((max, i) => Math.max(max, i.sortOrder ?? 0), -1);
        const row = {...newItem(), catalogItem: {id: name, name}, quantity, sortOrder: maxSortOrder + 1};
        setItems((prev) => [...prev, row]);
        setNewItemText('');
        registerPendingCommit(getShoppingItemKey(row), 'edit');
        setTimeout(() => newItemInputRef.current?.focus?.(), 50);
    }, [registerPendingCommit]);

    const parsedNewItemName = useMemo(() => parseItemText(newItemText).name, [newItemText]);

    const canCreate = () =>
        hasUserPermission(UserPermission.CREATE_SHOPPING_LIST)
        && hasCompanyPermission(CompanyPermission.CREATE_SHOPPING_LIST);

    const canEdit = () =>
        hasUserPermission(UserPermission.EDIT_SHOPPING_LIST)
        && hasCompanyPermission(CompanyPermission.EDIT_SHOPPING_LIST);


    const handleHeaderSort = useCallback((column) => {
        setSortConfig((prev) => {
            if (prev.primary.column === column) {
                return {
                    ...prev,
                    primary: {column, direction: prev.primary.direction === 'asc' ? 'desc' : 'asc'},
                };
            }
            return {
                primary: {column, direction: 'asc'},
                secondary: prev.primary.column != null ? prev.primary : null,
            };
        });
    }, []);

    const toggleObsRow = useCallback((key) => {
        setExpandedObsKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const togglePurchased = useCallback((key) => {
        const item = itemsRef.current.find((row) => getShoppingItemKey(row) === key);
        if (!item) {
            return;
        }

        if (purchasePendingRef.current.has(key) || purchasingAnimKeysRef.current.has(key)) {
            cancelPurchaseTransition(key);
            return;
        }

        const mode = item.purchased ? 'unmark' : 'mark';
        const nextPending = new Map(purchasePendingRef.current);
        nextPending.set(key, mode);
        purchasePendingRef.current = nextPending;
        setPurchasePendingKeys(nextPending);
        setTimeout(() => startPurchaseIdleCountdown(), 0);
        syncNow();
    }, [cancelPurchaseTransition, startPurchaseIdleCountdown, syncNow]);

    const applyTemplateItemsToTable = (sourceItems) => {
        const rows = (sourceItems || [])
            .filter((item) => item.itemName && item.itemName.trim() !== '')
            .map((item, index) => ({
                ...newItem(),
                catalogItem: {id: item.itemName, name: item.itemName},
                quantity: parseQuantity(item.quantity),
                observation: item.observation || '',
                supermarketSector: item.supermarketSector || '',
                purchased: false,
                sortOrder: index,
            }));
        setExpandedObsKeys(rebuildExpandedObsKeys(rows));
        setItems(rows);
    };

    async function loadDefaultItemsIntoTable() {
        try {
            const names = await makeRequest(`${apiUrl}/api/shopping-lists/default-items`, 'GET');
            const defaultRows = (names || []).map((name, index) => ({
                ...newItem(),
                catalogItem: {id: name, name},
                quantity: 1,
                sortOrder: index,
            }));
            setExpandedObsKeys(new Set());
            setItems(defaultRows);
        } catch (error) {
            console.error('Error loading default items:', error);
            setItems([newItem()]);
        }
    }

    async function loadDefaultTemplateIntoTable() {
        try {
            const template = await makeRequest(`${apiUrl}/api/shopping-lists/default-template`, 'GET');
            applyTemplateItemsToTable(template.items);
        } catch (error) {
            if (error?.statusCode !== 404) {
                console.error('Error loading default template list:', error);
            }
            await loadDefaultItemsIntoTable();
        }
    }



    async function loadList() {
        if (!id) {
            setFormData(newListEntity());
            await loadDefaultTemplateIntoTable();
            enableAutoSaveAndSchedule();
            return;
        }
        try {
            setLoading(true);
            const response = await makeRequest(`${apiUrl}/api/shopping-lists/id=${id}`, 'GET');
            setFormData({
                id: response.id,
                name: response.name,
                sharedUserIds: response.sharedUserIds || [],
                ownerUserId: response.ownerUserId,
                defaultTemplate: !!response.defaultTemplate,
            });
            const loadedItems = (response.items || []).map((item, index) => ({
                ...item,
                _clientKey: item.id ? `db-${item.id}` : nextShoppingItemClientKey(),
                catalogItem: {id: item.itemName, name: item.itemName},
                quantity: parseQuantity(item.quantity),
                supermarketSector: item.supermarketSector || '',
                purchased: !!item.purchased,
                sortOrder: item.sortOrder ?? index,
            }));
            setExpandedObsKeys(rebuildExpandedObsKeys(loadedItems));
            setItems(loadedItems);
            if (response.updatedAt) {
                lastKnownUpdatedAtRef.current = response.updatedAt;
            }
            skipAutoSaveRef.current = false;
        } catch (error) {
            console.error('Error loading shopping list:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (!loadingPermissions) {
            loadList();
        }
    }, [id, loadingPermissions]);

    const handleNameChange = (e) => {
        nameDirtyRef.current = true;
        setFormData((prev) => ({...prev, name: e.target.value}));
        registerPendingCommit(LIST_TITLE_KEY, 'edit');
    };

    const handleItemChange = useCallback((key, field, value) => {
        setItems((prev) => {
            const index = prev.findIndex((item) => getShoppingItemKey(item) === key);
            if (index < 0) return prev;
            const updated = [...prev];
            const current = updated[index];
            const next = {
                ...current,
                [field]: field === 'quantity' ? parseQuantity(value) : value,
            };
            // Troca de setor: congela a posição de ordenação até o commit (fade) terminar,
            // para o efeito vir antes da reordenação. Guarda o setor anterior em _sortSector.
            if (field === 'supermarketSector' && current._sortSector === undefined) {
                next._sortSector = current.supermarketSector || '';
            }
            updated[index] = next;
            return updated;
        });
        registerPendingCommit(key, 'edit');
    }, [registerPendingCommit]);

    const removeItem = useCallback((key) => {
        // Não remove na hora: marca pendência; a linha faz fade e some ao efetivar.
        registerPendingCommit(key, 'remove');
    }, [registerPendingCommit]);

    const selectItemName = useCallback((itemName, key) => {
        const name = (itemName || '').trim();
        if (!name) return;
        const normalized = name.toLowerCase();
        const isDup = itemsRef.current.some((item) =>
            getShoppingItemKey(item) !== key
            && item.catalogItem?.id
            && (item.catalogItem.name || '').trim().toLowerCase() === normalized
        );
        if (isDup) {
            showAlert('Este item já está na lista.');
            return;
        }
        setItems((prev) => {
            const index = prev.findIndex((item) => getShoppingItemKey(item) === key);
            if (index < 0) return prev;
            const updated = [...prev];
            updated[index] = {...updated[index], catalogItem: {id: name, name}, quantity: updated[index].quantity || 1};
            return updated;
        });
        registerPendingCommit(key, 'edit');
    }, [registerPendingCommit]);

    const handleItemInputChange = useCallback((inputValue, key) => {
        setItems((prev) => {
            const index = prev.findIndex((item) => getShoppingItemKey(item) === key);
            if (index < 0) return prev;
            const current = prev[index];
            const updated = [...prev];
            updated[index] = {
                ...current,
                catalogItem: {id: undefined, name: inputValue},
                quantity: current.quantity || 1,
            };
            return updated;
        });
    }, []);

    const commitItemNameOnBlur = useCallback((key) => {
        if (suppressItemBlurCommitRef.current) {
            return;
        }

        const currentItems = itemsRef.current;
        const index = currentItems.findIndex((row) => getShoppingItemKey(row) === key);
        if (index < 0) {
            return;
        }

        const item = currentItems[index];
        const name = (item.catalogItem?.name || '').trim();

        if (!name) {
            if (item.catalogItem?.id) {
                setItems((prev) => {
                    const idx = prev.findIndex((row) => getShoppingItemKey(row) === key);
                    if (idx < 0) return prev;
                    const updated = [...prev];
                    updated[idx] = {...prev[idx], catalogItem: {id: undefined, name: ''}};
                    return updated;
                });
                registerPendingCommit(key, 'edit');
            }
            return;
        }

        const normalized = name.toLowerCase();
        const isDup = currentItems.some((row, i) =>
            i !== index
            && row.catalogItem?.id
            && (row.catalogItem.name || '').trim().toLowerCase() === normalized
        );

        if (isDup) {
            showAlert('Este item já está na lista.');
            setItems((prev) => {
                const idx = prev.findIndex((row) => getShoppingItemKey(row) === key);
                if (idx < 0) return prev;
                const updated = [...prev];
                const prevItem = prev[idx];
                updated[idx] = {
                    ...prevItem,
                    catalogItem: prevItem.catalogItem?.id
                        ? {id: prevItem.catalogItem.id, name: prevItem.catalogItem.id}
                        : {id: undefined, name: ''},
                };
                return updated;
            });
            return;
        }

        if (item.catalogItem?.id === name) {
            return;
        }

        setItems((prev) => {
            const idx = prev.findIndex((row) => getShoppingItemKey(row) === key);
            if (idx < 0) return prev;
            const updated = [...prev];
            updated[idx] = {...prev[idx], catalogItem: {id: name, name}};
            return updated;
        });
        registerPendingCommit(key, 'edit');
    }, [registerPendingCommit]);

    const focusItemInput = useCallback((key) => {
        setTimeout(() => {
            itemInputRefs.current[key]?.focus?.();
        }, 50);
    }, []);

    const handleItemClick = useCallback((itemName, key) => {
        suppressItemBlurCommitRef.current = true;
        selectItemName(itemName, key);
        focusItemInput(key);
        setTimeout(() => {
            suppressItemBlurCommitRef.current = false;
        }, 200);
    }, [selectItemName, focusItemInput]);

    if (loadingPermissions) return <Loading />;

    const allowed = id ? canEdit() : canCreate();
    if (!allowed) {
        navigate('/unauthorizedaccess');
        return null;
    }

    const pickerCurrentSector = sectorPickerKey != null
        ? (items.find((i) => getShoppingItemKey(i) === sectorPickerKey)?.supermarketSector || '')
        : '';

    return (
        <div ref={managerRef} className="app-form-page shopping-list-manager">
            <div className="shopping-list-manager-header">
                <div className="header-label">{id || formData.id ? 'Editar' : 'Nova'} Lista de Compras</div>
                {saveStatus && (
                    <span className="shopping-list-save-status">{saveStatus}</span>
                )}
            </div>

            <div className="shopping-list-field">
                <label>Nome da lista</label>
                <input
                    type="text"
                    className={committingAnimKeys.has(LIST_TITLE_KEY) ? 'shopping-list-title-committing' : undefined}
                    value={formData.name}
                    onChange={handleNameChange}
                    placeholder="Ex.: Compras da semana"
                />
            </div>


            <div className="shopping-list-section">
                {!id && !formData.id && (
                    <p className="shopping-list-hint">
                        Os itens da sua lista padrão (se houver) ou do catálogo inicial serão copiados (qtd. 1).
                        Remova o que não precisar e adicione novos abaixo.
                    </p>
                )}
                <div className="shopping-list-add-row">
                    <ShoppingListItemSelectAsync
                        ref={newItemInputRef}
                        style={{flex: 1, minWidth: '120px', menuWidth: '220px'}}
                        textValue={newItemText}
                        searchValue={parsedNewItemName}
                        excludeNames={allFilledNames}
                        onItemClick={(name) => handleAddNewItem(name)}
                        onInputValueChange={(val) => setNewItemText(val)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAddNewItem();
                        }}
                        placeholder="Adicionar item..."
                        dataTest="shopping-list-new-item-input"
                    />
                    <button
                        type="button"
                        className="shopping-list-add-btn"
                        onClick={() => handleAddNewItem()}
                        data-test="shopping-list-add-btn"
                    >
                        Adicionar
                    </button>
                </div>
                <table className="shopping-list-items-table">
                    <thead>
                        <tr>
                            <SortableHeader
                                column="purchased"
                                label=""
                                sortConfig={sortConfig}
                                onSort={handleHeaderSort}
                                style={{width: '36px'}}
                            />
                            <SortableHeader
                                column="item"
                                label="Item"
                                sortConfig={sortConfig}
                                onSort={handleHeaderSort}
                                style={{width: 'auto', minWidth: '120px'}}
                            />
                            <SortableHeader
                                column="sector"
                                label="Setor"
                                sortConfig={sortConfig}
                                onSort={handleHeaderSort}
                                style={{width: '34px'}}
                            />
                            <SortableHeader
                                column="quantity"
                                label="Qtd."
                                sortConfig={sortConfig}
                                onSort={handleHeaderSort}
                                style={{width: '72px'}}
                            />
                            <th style={{width: '52px'}}/>
                        </tr>
                    </thead>
                    <tbody>
                        {displayItems.map((item) => {
                            const itemKey = getShoppingItemKey(item);
                            const purchased = !!item.purchased;
                            const pendingMode = purchasePendingKeys.get(itemKey);
                            const isPendingMark = pendingMode === 'mark';
                            const isPendingUnmark = pendingMode === 'unmark';
                            const isPurchasingAnim = purchasingAnimKeys.has(itemKey);
                            const animMode = purchasingAnimModes.get(itemKey);
                            const isAnimMark = isPurchasingAnim && animMode === 'mark';
                            const showPurchasedStyle = purchased || isPurchasingAnim;
                            const checkboxChecked = (purchased && !isPendingUnmark) || isPendingMark || isAnimMark;
                            const isCommitting = committingAnimKeys.has(itemKey);
                            const rowClass = [
                                showPurchasedStyle ? 'shopping-list-item-row--purchased' : '',
                                isPurchasingAnim ? 'shopping-list-item-row--purchasing-fade' : '',
                                isCommitting ? 'shopping-list-item-row--committing' : '',
                            ].filter(Boolean).join(' ');
                            const namePurchasedClass = showPurchasedStyle ? 'shopping-list-item-name--purchased' : '';
                            return (
                                <ShoppingListItemRow
                                    key={itemKey}
                                    itemKey={itemKey}
                                    item={item}
                                    obsExpanded={expandedObsKeys.has(itemKey)}
                                    checkboxChecked={checkboxChecked}
                                    isPurchasingAnim={isPurchasingAnim}
                                    purchased={purchased}
                                    isPendingUnmark={isPendingUnmark}
                                    rowClass={rowClass}
                                    namePurchasedClass={namePurchasedClass}
                                    excludeNames={filledItemNamesMap[itemKey] ?? []}
                                    itemInputRefs={itemInputRefs}
                                    sectorColorMap={sectorColorMap}
                                    onTogglePurchased={togglePurchased}
                                    onItemClick={handleItemClick}
                                    onInputValueChange={handleItemInputChange}
                                    onBlur={commitItemNameOnBlur}
                                    onItemChange={handleItemChange}
                                    onToggleObs={toggleObsRow}
                                    onRemove={removeItem}
                                    onOpenSectorPicker={openSectorPicker}
                                />
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {sectorPickerKey != null && (
                <div
                    className="shopping-list-sector-modal-overlay"
                    onClick={() => setSectorPickerKey(null)}
                >
                    <div
                        className="shopping-list-sector-modal"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="shopping-list-sector-modal-header">
                            <span>Selecionar setor</span>
                            <button
                                type="button"
                                className="shopping-list-sector-modal-close"
                                onClick={() => setSectorPickerKey(null)}
                                aria-label="Fechar"
                            >×</button>
                        </div>
                        <div className="shopping-list-sector-modal-options">
                            <button
                                type="button"
                                className="shopping-list-sector-option shopping-list-sector-option--none"
                                onClick={() => {
                                    handleItemChange(sectorPickerKey, 'supermarketSector', '');
                                    setSectorPickerKey(null);
                                }}
                            >
                                Sem setor
                            </button>
                            {sectorOptions.map((sector) => {
                                const c = resolveSectorColor(sector, sectorColorMap);
                                const selected = (pickerCurrentSector || '').trim().toLowerCase()
                                    === sector.trim().toLowerCase();
                                return (
                                    <button
                                        key={sector}
                                        type="button"
                                        className={`shopping-list-sector-option${selected ? ' shopping-list-sector-option--selected' : ''}`}
                                        style={{backgroundColor: c, color: sectorTextColor(c), borderColor: c}}
                                        onClick={() => {
                                            handleItemChange(sectorPickerKey, 'supermarketSector', sector);
                                            setSectorPickerKey(null);
                                        }}
                                    >
                                        {selected ? '✓ ' : ''}{sector}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShoppingListManager;
